/**
 * The background sync loop — what actually makes two phones show the same
 * board.
 *
 * A pass runs on three triggers, which between them cover how the app is really
 * used:
 *
 * 1. **On a timer**, every {@link SYNC_POLL_MS}. This is the one that matters
 *    for a commander watching the roster without touching it.
 * 2. **When the tab comes back to the foreground.** A phone that was in a
 *    pocket for ten minutes has stale data and one visible screen; waiting out
 *    the timer would show it stale for another eight seconds.
 * 3. **After an action**, through {@link CloudSync.syncNow}. A status tapped on
 *    this device should be on everybody else's within a moment, not within a
 *    poll.
 *
 * Failures are absorbed: a device with no backend simply reports `offline` and
 * backs off, and the shell keeps rendering Dexie. Nothing about this hook is
 * load-bearing for the application working.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { SYNC_POLL_MS, backoffDelay } from '../../core/sync-engine';
import type { SyncState } from '../../core/sync-engine';
import { onLocalWrite } from '../../core/sync-signal';
import { syncOnce } from '../../db/sync-store';

/**
 * How long a burst of local writes is allowed to settle before it is pushed.
 *
 * Saving a form writes the record and files a ledger row; a bulk action writes
 * several. Pushing on each one would send the same batch three times over, so
 * the trigger waits for the burst to stop — still far inside the "immediately"
 * the operator perceives.
 */
const WRITE_DEBOUNCE_MS = 400;

export interface CloudSync {
  state: SyncState;
  /** When the last successful pass finished, or `null` if none has. */
  lastSyncedAt: number | null;
  /** Runs a pass now — called after an action that changed something. */
  syncNow: () => void;
}

/**
 * @param enabled  Only a signed-in shell syncs; a visitor at the PIN screen has
 *                 nothing to mirror and no account to mirror it for.
 * @param onRemoteChange  Called after a pass that actually wrote something
 *                 locally, so the screens re-read. A pass that only echoed this
 *                 device's own writes back does not call it — re-rendering the
 *                 whole board every eight seconds would fight with whatever the
 *                 operator is typing.
 */
export function useCloudSync(enabled: boolean, onRemoteChange: () => void): CloudSync {
  const [state, setState] = useState<SyncState>('offline');
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);

  /** Consecutive failures — drives the backoff. */
  const failures = useRef(0);
  /** Guards against two passes overlapping when a tap lands mid-poll. */
  const running = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Kept in a ref so the loop never has to be torn down to see a new callback. */
  const notify = useRef(onRemoteChange);
  notify.current = onRemoteChange;
  /** Set on unmount, so a pass that is still in flight stops touching state. */
  const alive = useRef(true);

  const runPass = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    setState((current) => (current === 'error' ? current : 'syncing'));
    try {
      const result = await syncOnce();
      if (!alive.current) return;
      if (result.ok) {
        failures.current = 0;
        setState('idle');
        setLastSyncedAt(Date.now());
        if (result.changed) notify.current();
      } else {
        failures.current += 1;
        // The first failure is usually "there is no backend here" rather than
        // "the backend broke", so it reads as offline rather than as an error.
        setState(failures.current > 2 ? 'error' : 'offline');
      }
    } finally {
      running.current = false;
    }
  }, []);

  const syncNow = useCallback(() => {
    void runPass();
  }, [runPass]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) {
      setState('offline');
      return;
    }

    /** Re-arms the timer at whatever the current backoff says. */
    const schedule = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        await runPass();
        if (alive.current) schedule();
      }, backoffDelay(failures.current, SYNC_POLL_MS));
    };

    void runPass().then(() => {
      if (alive.current) schedule();
    });

    const onVisibility = () => {
      // Coming back to the foreground is the one moment the data is most
      // likely stale and most likely being looked at.
      if (document.visibilityState === 'visible') void runPass();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', syncNow);

    // The third trigger: this device just wrote something. Debounced, so a form
    // save that touches a record and its ledger row is one push, not two.
    let settle: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = onLocalWrite(() => {
      if (settle) clearTimeout(settle);
      settle = setTimeout(() => void runPass(), WRITE_DEBOUNCE_MS);
    });

    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      if (settle) clearTimeout(settle);
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', syncNow);
    };
  }, [enabled, runPass, syncNow]);

  return { state, lastSyncedAt, syncNow };
}
